import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, ChevronUp, Repeat } from 'lucide-react'
import type { RecurrencePattern } from '@/lib/types/recurrence'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  DaysOfWeek,
  RecurrenceType,
  getShortDayName,
} from '@/lib/types/recurrence'
import { cn } from '@/lib/utils'

/**
 * Props for the RecurrencePatternPicker component.
 */
interface RecurrencePatternPickerProps {
  /** Current recurrence pattern value */
  value: RecurrencePattern | null
  /** Callback when pattern changes */
  onChange: (pattern: RecurrencePattern | null) => void
  /** Additional CSS classes */
  className?: string
}

/**
 * Preset recurrence patterns for common use cases.
 * Provides quick selection options for typical recurring patterns.
 */
const PRESETS = {
  /** No recurrence */
  none: { label: 'Does not repeat', pattern: null },
  /** Every day */
  daily: {
    label: 'Daily',
    pattern: {
      recurrenceType: RecurrenceType.Daily,
      recurrenceInterval: 1,
      recurrenceDaysOfWeek: null,
      recurrenceDayOfMonth: null,
      recurrenceEndDate: null,
      recurrenceMaxOccurrences: null,
    },
  },
  /** Monday through Friday */
  weekday: {
    label: 'Every Weekday (Mon - Fri)',
    pattern: {
      recurrenceType: RecurrenceType.Weekly,
      recurrenceInterval: 1,
      recurrenceDaysOfWeek: 31, // Mon(1) + Tue(2) + Wed(4) + Thu(8) + Fri(16)
      recurrenceDayOfMonth: null,
      recurrenceEndDate: null,
      recurrenceMaxOccurrences: null,
    },
  },
  /** Saturday and Sunday */
  weekend: {
    label: 'Every Weekend (Sat - Sun)',
    pattern: {
      recurrenceType: RecurrenceType.Weekly,
      recurrenceInterval: 1,
      recurrenceDaysOfWeek: 96, // Sat(32) + Sun(64)
      recurrenceDayOfMonth: null,
      recurrenceEndDate: null,
      recurrenceMaxOccurrences: null,
    },
  },
  /** Every week */
  weekly: {
    label: 'Weekly',
    pattern: {
      recurrenceType: RecurrenceType.Weekly,
      recurrenceInterval: 1,
      recurrenceDaysOfWeek: 1 << new Date().getDay(), // Default to today's day
      recurrenceDayOfMonth: null,
      recurrenceEndDate: null,
      recurrenceMaxOccurrences: null,
    },
  },
  monthly: {
    label: 'Monthly',
    pattern: {
      recurrenceType: RecurrenceType.Monthly,
      recurrenceInterval: 1,
      recurrenceDaysOfWeek: null,
      recurrenceDayOfMonth: 1,
      recurrenceEndDate: null,
      recurrenceMaxOccurrences: null,
    },
  },
  yearly: {
    label: 'Yearly',
    pattern: {
      recurrenceType: RecurrenceType.Yearly,
      recurrenceInterval: 1,
      recurrenceDaysOfWeek: null,
      recurrenceDayOfMonth: null,
      recurrenceEndDate: null,
      recurrenceMaxOccurrences: null,
    },
  },
}

export function RecurrencePatternPicker({
  value,
  onChange,
  className,
}: RecurrencePatternPickerProps) {
  const { date: fmtDate } = useDateTimeFormatters()
  const [localPattern, setLocalPattern] = useState<RecurrencePattern | null>(
    value,
  )

  // Sync local state when value prop changes (e.g., when popover reopens)
  useEffect(() => {
    setLocalPattern(value)
  }, [value])

  const updatePattern = (updates: Partial<RecurrencePattern> | null) => {
    if (updates === null) {
      setLocalPattern(null)
      onChange(null)
      return
    }

    const newPattern = { ...localPattern!, ...updates }
    setLocalPattern(newPattern)
    onChange(newPattern)
  }

  const getRecurrenceLabel = (): string => {
    if (!localPattern || localPattern.recurrenceType === null) {
      return 'Does not repeat'
    }

    // Check for presets
    if (
      localPattern.recurrenceType === RecurrenceType.Daily &&
      localPattern.recurrenceInterval === 1
    ) {
      return 'Daily'
    }

    if (
      localPattern.recurrenceType === RecurrenceType.Weekly &&
      localPattern.recurrenceInterval === 1 &&
      localPattern.recurrenceDaysOfWeek === 31
    ) {
      return 'Every Weekday (Mon - Fri)'
    }

    if (
      localPattern.recurrenceType === RecurrenceType.Weekly &&
      localPattern.recurrenceInterval === 1 &&
      localPattern.recurrenceDaysOfWeek === 96
    ) {
      return 'Every Weekend (Sat - Sun)'
    }

    if (
      localPattern.recurrenceType === RecurrenceType.Weekly &&
      localPattern.recurrenceInterval === 1
    ) {
      // Show selected days for custom weekly pattern
      if (localPattern.recurrenceDaysOfWeek) {
        const days: Array<string> = []
        if (localPattern.recurrenceDaysOfWeek & DaysOfWeek.Monday)
          days.push('Mon')
        if (localPattern.recurrenceDaysOfWeek & DaysOfWeek.Tuesday)
          days.push('Tue')
        if (localPattern.recurrenceDaysOfWeek & DaysOfWeek.Wednesday)
          days.push('Wed')
        if (localPattern.recurrenceDaysOfWeek & DaysOfWeek.Thursday)
          days.push('Thu')
        if (localPattern.recurrenceDaysOfWeek & DaysOfWeek.Friday)
          days.push('Fri')
        if (localPattern.recurrenceDaysOfWeek & DaysOfWeek.Saturday)
          days.push('Sat')
        if (localPattern.recurrenceDaysOfWeek & DaysOfWeek.Sunday)
          days.push('Sun')

        if (days.length > 0) {
          return `Weekly on ${days.join(', ')}`
        }
      }
      return 'Weekly'
    }

    if (
      localPattern.recurrenceType === RecurrenceType.Monthly &&
      localPattern.recurrenceInterval === 1
    ) {
      return 'Monthly'
    }

    if (
      localPattern.recurrenceType === RecurrenceType.Yearly &&
      localPattern.recurrenceInterval === 1
    ) {
      return 'Yearly'
    }

    return 'Custom'
  }

  const getEndLabel = (): string => {
    if (!localPattern) return 'Never'

    if (localPattern.recurrenceMaxOccurrences) {
      return `After ${localPattern.recurrenceMaxOccurrences} times`
    }

    if (localPattern.recurrenceEndDate) {
      return `Until ${fmtDate(new Date(localPattern.recurrenceEndDate))}`
    }

    return 'Endlessly'
  }

  const isRecurring = localPattern && localPattern.recurrenceType !== null

  return (
    <div className={cn('space-y-0', className)}>
      {/* Recurrence Type Row */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors"
          >
            <div className="flex items-center gap-2">
              <Repeat className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-foreground">
                {getRecurrenceLabel()}
              </span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuItem
            onClick={() => {
              setLocalPattern(null)
              onChange(null)
            }}
          >
            Does not repeat
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const pattern = PRESETS.daily.pattern
              setLocalPattern(pattern)
              onChange(pattern)
            }}
          >
            Daily
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const pattern = PRESETS.weekday.pattern
              setLocalPattern(pattern)
              onChange(pattern)
            }}
          >
            Every Weekday (Mon - Fri)
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const pattern = PRESETS.weekend.pattern
              setLocalPattern(pattern)
              onChange(pattern)
            }}
          >
            Every Weekend (Sat - Sun)
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const pattern = PRESETS.weekly.pattern
              setLocalPattern(pattern)
              onChange(pattern)
            }}
          >
            Weekly
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const pattern = PRESETS.monthly.pattern
              setLocalPattern(pattern)
              onChange(pattern)
            }}
          >
            Monthly
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const pattern = PRESETS.yearly.pattern
              setLocalPattern(pattern)
              onChange(pattern)
            }}
          >
            Yearly
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Day Selector - Only shows for weekly recurrence (not for presets) */}
      {isRecurring &&
        localPattern.recurrenceType === RecurrenceType.Weekly &&
        localPattern.recurrenceDaysOfWeek !== 31 && // Not weekday preset
        localPattern.recurrenceDaysOfWeek !== 96 && ( // Not weekend preset
          <div className="px-3 py-2">
            <div className="text-xs text-muted-foreground mb-2">Repeat on</div>
            <div className="flex gap-1">
              {[
                {
                  value: DaysOfWeek.Monday,
                  label: getShortDayName(DaysOfWeek.Monday),
                },
                {
                  value: DaysOfWeek.Tuesday,
                  label: getShortDayName(DaysOfWeek.Tuesday),
                },
                {
                  value: DaysOfWeek.Wednesday,
                  label: getShortDayName(DaysOfWeek.Wednesday),
                },
                {
                  value: DaysOfWeek.Thursday,
                  label: getShortDayName(DaysOfWeek.Thursday),
                },
                {
                  value: DaysOfWeek.Friday,
                  label: getShortDayName(DaysOfWeek.Friday),
                },
                {
                  value: DaysOfWeek.Saturday,
                  label: getShortDayName(DaysOfWeek.Saturday),
                },
                {
                  value: DaysOfWeek.Sunday,
                  label: getShortDayName(DaysOfWeek.Sunday),
                },
              ].map((day) => {
                const isSelected =
                  localPattern?.recurrenceDaysOfWeek &&
                  (localPattern.recurrenceDaysOfWeek & day.value) !== 0
                return (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => {
                      const current = localPattern?.recurrenceDaysOfWeek ?? 0
                      const newValue = current ^ day.value
                      updatePattern({
                        recurrenceDaysOfWeek: newValue || null,
                      })
                    }}
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded text-xs font-medium transition-colors',
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-accent',
                    )}
                  >
                    {day.label}
                  </button>
                )
              })}
            </div>
          </div>
        )}

      {/* End Condition Row - Only shows if recurring */}
      {isRecurring && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors"
            >
              <div className="flex items-center gap-2">
                <Repeat className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-foreground">
                  {getEndLabel()}
                </span>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground/70" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem
              onClick={() =>
                updatePattern({
                  recurrenceEndDate: null,
                  recurrenceMaxOccurrences: null,
                })
              }
            >
              Endlessly
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                updatePattern({
                  recurrenceEndDate: new Date(
                    Date.now() + 30 * 24 * 60 * 60 * 1000,
                  )
                    .toISOString()
                    .split('T')[0],
                  recurrenceMaxOccurrences: null,
                })
              }
            >
              On date...
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                updatePattern({
                  recurrenceEndDate: null,
                  recurrenceMaxOccurrences: 10,
                })
              }
            >
              After N times
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Repeat Count Input - Shows when "After N times" is selected */}
      {isRecurring && localPattern?.recurrenceMaxOccurrences !== null && (
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="1"
              max="999"
              value={localPattern?.recurrenceMaxOccurrences ?? 10}
              onChange={(e) => {
                const count = parseInt(e.target.value) || 1
                updatePattern({ recurrenceMaxOccurrences: count })
              }}
              className="w-16 h-8 text-center"
            />
            <div className="flex flex-col">
              <button
                type="button"
                onClick={() => {
                  const current = localPattern?.recurrenceMaxOccurrences ?? 10
                  updatePattern({ recurrenceMaxOccurrences: current + 1 })
                }}
                className="h-4 flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <ChevronUp className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => {
                  const current = localPattern?.recurrenceMaxOccurrences ?? 10
                  if (current > 1) {
                    updatePattern({ recurrenceMaxOccurrences: current - 1 })
                  }
                }}
                className="h-4 flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <ChevronDown className="h-3 w-3" />
              </button>
            </div>
          </div>
          <span className="text-sm text-foreground">Repeat Times</span>
        </div>
      )}

      {/* Date Input - Shows when "On date" is selected */}
      {isRecurring && localPattern?.recurrenceEndDate && (
        <div className="flex items-center gap-2 px-3 py-2">
          <Input
            type="date"
            value={localPattern.recurrenceEndDate}
            onChange={(e) =>
              updatePattern({ recurrenceEndDate: e.target.value })
            }
            onClick={(e) => {
              const input = e.currentTarget as HTMLInputElement & {
                showPicker?: () => void
              }
              input.showPicker?.()
            }}
            className="flex-1"
          />
        </div>
      )}
    </div>
  )
}
